import PropTypes from "prop-types";
import { createContext, useEffect, useReducer } from "react";

// reducer - state management
import { LOGIN, LOGOUT } from "store/actions";
import accountReducer from "store/accountReducer";

// project imports
import Loader from "ui-component/Loader";
import axios from "utils/axios";
import {
  clearAdminSession,
  scheduleAdminSessionTimeout,
  verifyAdminToken,
} from "utils/adminSession";

// constant
const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
};

// บุคลากรโรงเรียนต้องเข้าสู่ระบบผ่านลิงก์ School System เท่านั้น
const SCHOOL_ROLES = ["school_admin", "school_executive", "school_teacher"];
const isSchoolRole = (admin) => SCHOOL_ROLES.includes(admin?.role);
const SCHOOL_PORTAL_ONLY_MESSAGE =
  "บัญชีบุคลากรโรงเรียนไม่สามารถเข้าสู่ระบบผ่านลิงก์นี้ได้ กรุณาเข้าสู่ระบบผ่านลิงก์ระบบโรงเรียน (School System)";

const setSession = (serviceToken) => {
  if (serviceToken) {
    window.localStorage.setItem("serviceToken", serviceToken);
    axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  } else {
    clearAdminSession();
    delete axios.defaults.headers.common.Authorization;
  }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //
const JWTContext = createContext(null);

export const JWTProvider = ({ children }) => {
  const [state, dispatch] = useReducer(accountReducer, initialState);

  const expireSession = () => {
    setSession(null);
    dispatch({ type: LOGOUT });
    window.location.replace("/");
  };

  useEffect(() => {
    const init = async () => {
      try {
        const serviceToken = window.localStorage.getItem("serviceToken");
        if (serviceToken && verifyAdminToken(serviceToken)) {
          setSession(serviceToken);
          scheduleAdminSessionTimeout(serviceToken, expireSession);
          const response = await axios.get("/auth/me");
          const admin = response.data?.data?.admin;
          if (admin && !isSchoolRole(admin)) {
            dispatch({
              type: LOGIN,
              payload: {
                isLoggedIn: true,
                user: admin,
              },
            });
            return;
          }
        }
        setSession(null);
        dispatch({ type: LOGOUT });
      } catch (err) {
        console.error("Auth init failed", err);
        setSession(null);
        dispatch({ type: LOGOUT });
      }
    };

    init();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post("/auth/login", {
      email,
      password,
      portal: "admin",
    });
    const { token, admin } = response.data?.data || {};
    if (!token || !admin) {
      throw new Error("Invalid response from authentication service");
    }
    if (isSchoolRole(admin)) {
      throw new Error(SCHOOL_PORTAL_ONLY_MESSAGE);
    }
    setSession(token);
    scheduleAdminSessionTimeout(token, expireSession);
    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        user: admin,
      },
    });
    return admin;
  };

  const loginWithServiceToken = async (serviceToken) => {
    if (!serviceToken) {
      throw new Error("serviceToken is required");
    }

    setSession(serviceToken);
    scheduleAdminSessionTimeout(serviceToken, expireSession);

    try {
      const response = await axios.get("/auth/me");
      const admin = response.data?.data?.admin;
      if (!admin) {
        throw new Error("Invalid response from authentication service");
      }
      if (isSchoolRole(admin)) {
        throw new Error(SCHOOL_PORTAL_ONLY_MESSAGE);
      }
      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          user: admin,
        },
      });
      return admin;
    } catch (err) {
      setSession(null);
      throw err;
    }
  };

  const register = async () => {
    throw new Error("Superadmins handle admin onboarding manually.");
  };

  const logout = () => {
    setSession(null);
    dispatch({ type: LOGOUT });
  };

  const resetPassword = async () => {
    throw new Error("Contact a superadmin to reset your password.");
  };

  const updateProfile = () => {};

  if (!state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider
      value={{
        ...state,
        login,
        loginWithServiceToken,
        logout,
        register,
        resetPassword,
        updateProfile,
      }}
    >
      {children}
    </JWTContext.Provider>
  );
};

JWTProvider.propTypes = {
  children: PropTypes.node,
};

export default JWTContext;
